// Institute of Design and Innovation (email idi@unm.edu). //

export default class TimeService {
  /**
   * Returns a string representation of the given number of minutes in hours and minutes
   * ex. 90 -> 1 hour 30 minutes
   * @param minutes Number of minutes
   */
  static minutesToHoursFormatted (minutes: number): string {
    const hours = Math.floor(minutes / 60)
    const leftoverMinutes = minutes - (hours * 60)
    const hourString = `${hours} hour${hours > 1 ? 's' : ''}`

    if (leftoverMinutes > 0) {
      const minuteString = `${leftoverMinutes} minute${leftoverMinutes > 1 ? 's' : ''}`
      return hourString + ' ' + minuteString
    } else {
      return hourString
    }
  }

  /**
   * Converts a time represented as a string into the number of minutes since the start of day
   * @param timeString The time represented as a string
   */
  static convertTimeFromString (timeString: string): number | null {
    const regex = new RegExp(/((\d{1})|(\d{1,2}:\d{2}))(am|pm)/i)
    const match = regex.test(timeString)
    if (!match) {
      return null
    }

    let hour, minute

    const split = timeString.split(':')
    if (split.length === 1 && timeString.length === 4) {
      hour = Number(split[0].substr(0, 2))
      minute = 0
    } else if (split.length === 1) {
      hour = Number(split[0][0])
      minute = 0
    } else {
      hour = Number(split[0])
      minute = (Number(split[1].substr(0, 2)))
    }

    const mer = timeString.substr(timeString.length - 2, 2).toLowerCase()

    if (hour === 12 && mer === 'am') {
      hour = 0
    }

    let num = minute + 60 * hour
    if (mer === 'pm') {
      if (hour !== 12) {
        num += 60 * 12
      }
    }

    return num
  }

  /**
   * Converts a time represented as a number to a string
   * @param timeNum Time represented as the number of minutes since the start of day
   */
  static convertTimeToString (timeNum: number): string | null {
    let hour = Math.floor(timeNum / 60)
    const minute = timeNum - hour * 60
    let mer = 'am'

    if (hour > 12) {
      mer = 'pm'
      hour -= 12
    } else if (hour === 12) {
      mer = 'pm'
      hour = 12
    }

    if (hour === 0) {
      mer = 'am'
      hour = 12
    }

    const hourString = String(hour)
    const minuteString = minute >= 10 ? String(minute) : `0${minute}`

    return `${hourString}:${minuteString}${mer}`
  }

  static convertDurationToString (timeNum: number): string {
    const hour = Math.floor(timeNum / 60)
    const minute = timeNum - hour * 60
    const hourString = String(hour)
    const minString = String(minute)

    let duration = `${hourString} hrs`
    if (minute > 0) {
      duration = duration + ` ${minString} mins`
    }
    return duration
  }

  /**
   * Determins whether the given time string represents a valid time
   * @param timeString Time represtned as a string
   */
  static isValidTimeString (timeString: string): boolean {
    const regex = new RegExp(/((\d{1})|(\d{1,2}:\d{2}))(am|pm)/i)
    const match = regex.test(timeString)
    if (!match) {
      return false
    }

    let hour, minute

    const split = timeString.split(':')
    if (split.length === 1 && timeString.length === 4) {
      hour = Number(split[0].substr(0, 2))
      minute = 0
    } else if (split.length === 1) {
      hour = Number(split[0][0])
      minute = 0
    } else {
      hour = Number(split[0])
      minute = (Number(split[1].substr(0, 2)))
    }

    return (hour > 0 && hour <= 12 && minute < 60)
  }
}
